import { DOCUMENT, NgOptimizedImage } from '@angular/common';
import { Component, computed, effect, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { UiButtonComponent, UIIconComponent } from '@design-system';
import { NumberComponent } from '@shared-ui';
import { calculatePricePositionPercent } from '@tools';
import { BottomSheetComponent } from '../../../../components/bottom-sheet/bottom-sheet.component';
import { OrderFormProducerService } from '../../../../events/order-form/order-form-producer.service';
import { Symbol } from '../../../../models/symbol.model';
import { mapToSessionClosingPrice } from '../../../market-depth-best-limit/models/market-depth.model';
import { MarketViewMessage } from '../../../market/services/market-view.model';
import { ThemeService } from '../../../../services/theme.service';

export interface SymbolDetailCandleCardData {
	openingPrice: number;
	highPrice: number;
	lowPrice: number;
	closingPrice: number;
	lastPrice: number;
	previousClosePrice: number;
	highAllowedPrice: number; // highThreshold
	lowAllowedPrice: number; // lowThreshold
}

@Component({
	selector: 'app-symbol-detail-candle-card',
	imports: [NumberComponent, UiButtonComponent, UIIconComponent, BottomSheetComponent, NgOptimizedImage],
	templateUrl: './symbol-detail-candle-card.component.html',
	styleUrl: './symbol-detail-candle-card.component.scss',
	host: {
		class: 'block w-full'
	}
})
export class SymbolDetailCandleCardComponent {
	private readonly themeService = inject(ThemeService);
	private readonly orderFormProducer = inject(OrderFormProducerService);
	private readonly document = inject(DOCUMENT);

	constructor() {
		effect(onCleanup => {
			if (!this.showTooltip()) {
				return;
			}

			const dismiss = (event: PointerEvent): void => {
				if (!this.priceBar()?.nativeElement.contains(event.target as Node)) {
					this.hideTooltip();
				}
			};

			this.document.addEventListener('pointerdown', dismiss);
			onCleanup(() => this.document.removeEventListener('pointerdown', dismiss));
		});
	}

	readonly showHelp = input(true);
	/** When true, bar clicks push price into the order-form event bus. */
	readonly syncOrderForm = input(false);

	readonly symbolMeta = input<Symbol | undefined>(undefined);
	readonly liveMarketView = input<MarketViewMessage | undefined>(undefined);

	readonly candleHelpImageSrc = computed(() =>
		this.themeService.isDark() ? '/assets/images/candle-help-dark.svg' : '/assets/images/candle-help-light.svg'
	);

	readonly candleData = computed<SymbolDetailCandleCardData>(() => {
		const meta = this.symbolMeta();
		const live = this.liveMarketView();

		// `||` (not `??`): a pre-first-trade live tick sends 0 for these, and 0 should still
		// fall back to the REST snapshot rather than pin the candle to the low edge of the band.
		return {
			openingPrice: live?.ftp || meta?.firstTradedPrice || 0,
			highPrice: live?.htp || meta?.highTradedPrice || 0,
			lowPrice: live?.lwtp || meta?.lowTradedPrice || 0,
			closingPrice: mapToSessionClosingPrice(live, meta),
			lastPrice: live?.ltp || meta?.lastTradedPrice || 0,
			previousClosePrice: live?.lsp ?? meta?.lastSettlementPrice ?? 0,
			highAllowedPrice: meta?.highThreshold ?? 0,
			lowAllowedPrice: meta?.lowThreshold ?? 0
		};
	});

	/** Tradable price step; 0 (or missing) means prices are not snapped to a grid. */
	readonly tickSize = computed(() => this.symbolMeta()?.tickSize ?? 0);

	readonly priceBar = viewChild<ElementRef<HTMLDivElement>>('priceBar');
	readonly tooltipEl = viewChild<ElementRef<HTMLDivElement>>('tooltipEl');
	readonly candleInfoBottomSheet = viewChild('candleInfoBottomSheet', { read: BottomSheetComponent });

	/** Keeps the rotated 10px arrow inside the tooltip, clear of rounded corners. */
	private static readonly ARROW_INSET_PX = 12;

	/** How far the tooltip may overhang the bar edges (keeps page from scrolling). */
	private static readonly MAX_OVERFLOW_PX = 12;

	/** Arrow-key step used when the symbol has no tick grid to size one from. */
	private static readonly FALLBACK_STEP_PERCENT = 1;

	private static readonly CANDLE_LEARNING_URL = 'https://learning.emofid.com/candle-in-orbis/';

	tooltip = signal({
		tooltipPosition: '',
		tooltipArrowPosition: '50%',
		tooltipPrice: 0,
		tooltipPercent: 0
	});

	showTooltip = signal(false);

	/** Drag-knob position, as a percent of the allowed-price band. Starts centred on the bar. */
	readonly knobPercent = signal(50);

	/** The tradable price the knob currently sits on. */
	readonly knobPrice = computed(() => {
		const { lowAllowedPrice: min, highAllowedPrice: max } = this.candleData();

		return this.snapPriceToTick(min + ((max - min) * this.knobPercent()) / 100);
	});

	/**
	 * Arrow-key step, expressed as a percent of the allowed band so it can be added to
	 * `knobPercent`, but sized so one press moves `knobPrice` by exactly one tick. A fixed
	 * percent step would skip tradable prices on a wide band and, on a band only a few
	 * ticks across, snap two presses onto the same price with `aria-valuenow` unchanged.
	 * Without a tick grid there is no such unit, so the knob keeps its coarse percent step.
	 */
	private readonly keyboardStepPercent = computed(() => {
		const { lowAllowedPrice: min, highAllowedPrice: max } = this.candleData();
		const band = max - min;
		const tick = this.tickSize();

		if (!(tick > 0) || band <= 0) {
			return SymbolDetailCandleCardComponent.FALLBACK_STEP_PERCENT;
		}

		return (tick / band) * 100;
	});

	private isDragging = false;

	readonly isExpanded = signal(false);

	readonly priceDetails = computed(() => {
		const data = this.candleData();
		return [
			{ label: 'بیشترین', value: data.highPrice },
			{ label: 'آخرین', value: data.lastPrice },
			{ label: 'اولین', value: data.openingPrice },
			{ label: 'کمترین', value: data.lowPrice }
		];
	});

	readonly isBullish = computed(() => {
		const { lastPrice, openingPrice } = this.candleData();
		return lastPrice === openingPrice ? lastPrice > 0 : lastPrice > openingPrice;
	});

	readonly lastPricePercent = computed(() => {
		if (this.candleData()?.lowAllowedPrice === this.candleData()?.highAllowedPrice) {
			return 50;
		}
		return calculatePricePositionPercent(this.candleData()?.lastPrice ?? 0, {
			min: this.candleData()?.lowAllowedPrice ?? 0,
			max: this.candleData()?.highAllowedPrice ?? 0
		});
	});

	readonly closingPricePercent = computed(() => {
		if (this.candleData()?.lowAllowedPrice === this.candleData()?.highAllowedPrice) {
			return 50;
		}
		return calculatePricePositionPercent(this.candleData()?.closingPrice ?? 0, {
			min: this.candleData()?.lowAllowedPrice ?? 0,
			max: this.candleData()?.highAllowedPrice ?? 0
		});
	});

	readonly bodyShapePosition = computed(() => {
		const openingPricePercent = calculatePricePositionPercent(this.candleData()?.openingPrice ?? 0, {
			min: this.candleData()?.lowAllowedPrice ?? 0,
			max: this.candleData()?.highAllowedPrice ?? 0
		});

		const lastPricePercent = this.lastPricePercent();

		return {
			left: Math.min(openingPricePercent, lastPricePercent),
			right: 100 - Math.max(openingPricePercent, lastPricePercent)
		};
	});

	readonly shadowShapePosition = computed(() => {
		const lowPricePercent = calculatePricePositionPercent(this.candleData()?.lowPrice ?? 0, {
			min: this.candleData()?.lowAllowedPrice ?? 0,
			max: this.candleData()?.highAllowedPrice ?? 0
		});

		const highPricePercent = calculatePricePositionPercent(this.candleData()?.highPrice ?? 0, {
			min: this.candleData()?.lowAllowedPrice ?? 0,
			max: this.candleData()?.highAllowedPrice ?? 0
		});

		return {
			left: Math.min(lowPricePercent, highPricePercent),
			right: 100 - Math.max(lowPricePercent, highPricePercent)
		};
	});

	hideTooltip(): void {
		this.showTooltip.set(false);
	}

	onPriceBarPointerDown(event: PointerEvent): void {
		const element = this.priceBar()?.nativeElement;

		if (!element) {
			return;
		}

		this.isDragging = true;
		element.setPointerCapture(event.pointerId);
		this.moveKnobToClientX(event.clientX);
		this.publishPriceToOrderForm(this.knobPrice());
	}

	onPriceBarPointerMove(event: PointerEvent): void {
		if (this.isDragging) {
			this.moveKnobToClientX(event.clientX);
			this.publishPriceToOrderForm(this.knobPrice());
		}
	}

	onPriceBarPointerUp(event: PointerEvent): void {
		if (!this.isDragging) {
			return;
		}

		this.isDragging = false;
		this.moveKnobToClientX(event.clientX);
		this.publishPriceToOrderForm(this.knobPrice());
	}

	onPriceBarPointerLeave(event: PointerEvent): void {
		if (event.pointerType === 'mouse' && !this.isDragging) {
			this.hideTooltip();
		}
	}

	onPriceBarPointerCancel(): void {
		this.isDragging = false;
		this.hideTooltip();
	}

	onKnobKeydown(event: KeyboardEvent): void {
		const step = this.keyboardStepPercent();
		const percent = this.knobPercent();

		const next = {
			ArrowLeft: percent - step,
			ArrowRight: percent + step,
			Home: 0,
			End: 100
		}[event.key];

		if (next === undefined) {
			return;
		}

		event.preventDefault();
		this.moveKnobToPercent(next);
		this.publishPriceToOrderForm(this.knobPrice());
	}

	private moveKnobToClientX(clientX: number): void {
		const rect = this.priceBar()?.nativeElement.getBoundingClientRect();

		if (!rect?.width) {
			return;
		}

		this.moveKnobToPercent(((clientX - rect.left) / rect.width) * 100);
	}

	private moveKnobToPercent(percent: number): void {
		const rect = this.priceBar()?.nativeElement.getBoundingClientRect();

		if (!rect?.width) {
			return;
		}

		const clamped = Math.max(0, Math.min(100, percent));

		this.knobPercent.set(clamped);
		this.updateTooltip(rect.left + (rect.width * clamped) / 100);
	}

	toggleExpanded(): void {
		this.isExpanded.update(expanded => !expanded);
	}

	openCandleInfoBottomSheet(event: Event): void {
		event.stopPropagation();
		this.candleInfoBottomSheet()?.open();
	}

	openCandleLearningPage(): void {
		this.document.defaultView?.open(
			SymbolDetailCandleCardComponent.CANDLE_LEARNING_URL,
			'_blank',
			'noopener,noreferrer'
		);
	}

	onReferencePriceClick(price: number): void {
		if (!price) {
			return;
		}

		// Reference prices (thresholds, previous close) are not guaranteed to sit on the
		// tick grid, so they get snapped like a bar click before reaching the form.
		this.publishPriceToOrderForm(this.snapPriceToTick(price));
	}

	private publishPriceToOrderForm(price: number): void {
		if (!this.syncOrderForm() || !price) {
			return;
		}

		this.orderFormProducer.updateOrderForm({ price });
	}

	/**
	 * Snaps a price onto the symbol's tick grid (an order price must be a multiple of
	 * `tickSize`), then pulls it back inside the allowed band so the bar edges can never
	 * produce a price the exchange would reject.
	 */
	private snapPriceToTick(price: number): number {
		const tick = this.tickSize();

		if (!(tick > 0)) {
			return Math.round(price);
		}

		const { lowAllowedPrice: min, highAllowedPrice: max } = this.candleData();

		const snapped = Math.round(price / tick) * tick;
		const lowestTick = Math.ceil(min / tick) * tick;
		const highestTick = Math.floor(max / tick) * tick;

		// Band narrower than one tick: no valid grid point to clamp to, keep the snapped value.
		if (lowestTick > highestTick) {
			return snapped;
		}

		return Math.min(highestTick, Math.max(lowestTick, snapped));
	}

	private updateTooltip(clientX: number): void {
		const element = this.priceBar()?.nativeElement;

		if (!element) {
			return;
		}

		const rect = element.getBoundingClientRect();

		const positionPercent = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));

		const { lowAllowedPrice: min, highAllowedPrice: max } = this.candleData();

		const price = this.snapPriceToTick(min + ((max - min) * positionPercent) / 100);

		const previousClose = this.candleData()?.previousClosePrice ?? 1;

		const percent = ((price - previousClose) / previousClose) * 100;

		const cursorX = (positionPercent / 100) * rect.width;
		const tooltipElement = this.tooltipEl()?.nativeElement;
		// Border-box for centering the tooltip; padding-box for arrow `left` (absolute pos).
		const tooltipOffsetWidth = tooltipElement?.offsetWidth ?? 0;
		const tooltipClientWidth = tooltipElement?.clientWidth ?? 0;
		const halfWidth = tooltipOffsetWidth / 2;
		const arrowInset = SymbolDetailCandleCardComponent.ARROW_INSET_PX;
		const maxOverflow = SymbolDetailCandleCardComponent.MAX_OVERFLOW_PX;

		let tooltipX = cursorX;
		// Prefer % when centered so digit-width changes don't shift the arrow.
		let tooltipArrowPosition = '50%';
		if (tooltipOffsetWidth && rect.width > tooltipOffsetWidth) {
			// Mostly keep the tooltip inside the bar; allow only a few px of overhang
			// so the arrow can sit closer to the cursor at the ends.
			const minCenter = halfWidth - maxOverflow;
			const maxCenter = rect.width - halfWidth + maxOverflow;
			tooltipX = Math.max(minCenter, Math.min(maxCenter, cursorX));

			if (tooltipX !== cursorX && tooltipClientWidth) {
				const arrowMax = Math.max(arrowInset, tooltipClientWidth - arrowInset);
				const arrowX = Math.max(arrowInset, Math.min(arrowMax, cursorX - tooltipX + halfWidth));
				tooltipArrowPosition = arrowX + 'px';
			}
		}

		this.tooltip.set({
			tooltipPrice: price,
			tooltipPercent: percent,
			tooltipPosition: tooltipX + 'px',
			tooltipArrowPosition
		});

		if (!this.showTooltip()) {
			this.showTooltip.set(true);

			requestAnimationFrame(() => this.updateTooltip(clientX));
		}
	}
}
